import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Form, MenuProps, message } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import dayjs from 'dayjs';
import { options } from 'config/config';
import { Button, Checkbox, Dropdown, Icon, Table } from 'ui';
import { DrawerCreateDelivery } from 'ui/Drawer/Drawers/DrawerCreateDelivery';
import useQueryApiClient from 'utils/useQueryApiClient';
import { convertToDayJs } from 'utils/timeHelpers';
import { DeliveryProps, DefaultHoursProps } from './types';
import { StyledFarmDeliveries, PageLayoutWrapper } from './style';
import { useAuth } from '../../../hooks/useAuth';
import { ResponsiveWrapper } from '../ResponsiveWrapper';
import MobileList from '../MobileLayout/MobileList';
import DefaultLayout from '../DefaultLayout';
import { StyledMobileHeaderWrapper } from '../MobileLayout/style';

interface FarmDeliveriesProps {
  farmId?: number;
  showPageLayout?: boolean;
}

const FarmDeliveries: React.FC<FarmDeliveriesProps> = ({
  farmId,
  showPageLayout = false,
}) => {
  const [reloadCount, setReloadCount] = useState<number>(0);
  const [isOpenCreateDelivery, setIsOpenCreateDelivery] =
    useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const [form] = useForm();
  const intl = useIntl();
  const { can } = useAuth();

  const stableFilter = useMemo(() => {
    return farmId ? { farmId } : undefined;
  }, [farmId]);

  const { appendData: deleteDelivery } = useQueryApiClient({
    request: {
      url: `/api/v2/deliveries/:id`,
      method: 'DELETE',
    },
    onSuccess: () => handleSuccessDelete(),
    onError: () => handleError(),
  });

  const { appendData: fetchDelivery } = useQueryApiClient({
    request: {
      url: `/api/v2/deliveries/:id`,
      disableOnMount: true,
    },
    onSuccess: (response) => handleSuccessFetch(response),
    onError: () => handleError(),
  });

  const handleError = () => {
    message.error(`error`);
    handleReload();
  };
  const handleSuccessDelete = () => {
    message.success(
      intl.formatMessage({ id: 'message.success_delete_delivery' })
    );
    handleReload();
  };

  const handleSuccessFetch = async (response: DeliveryProps) => {
    let parsedOpenHours;
    const { open_hours, ...otherData } = response;
    const { address, ...otherDataFinal } = otherData;
    if (typeof open_hours === 'string') {
      parsedOpenHours = open_hours ? JSON.parse(open_hours) : {};
    }

    const defaultHours: DefaultHoursProps[] = Array.from({ length: 7 }, () => ({
      no: null,
      lidz: null,
      closed: false,
    }));

    for (let dayNumber = 1; dayNumber <= 7; dayNumber++) {
      const dayIndex = dayNumber - 1;
      const dataForDay = parsedOpenHours[String(dayNumber)];
      if (!dataForDay) continue;

      defaultHours[dayIndex].closed = dataForDay.closed === '1';

      defaultHours[dayIndex].no = dataForDay.no
        ? dayjs(dataForDay.no, 'HH:mm')
        : null;

      defaultHours[dayIndex].lidz = dataForDay.lidz
        ? dayjs(dataForDay.lidz, 'HH:mm')
        : null;
    }

    const finalValues: DeliveryProps = {
      ...otherDataFinal,
      open_hours: defaultHours,
    };

    if (address && address.trim() !== '') {
      finalValues.address = address;
    }

    form.setFieldsValue(finalValues);

    setIsOpenCreateDelivery(true);
  };

  const handleEdit = (record: DeliveryProps) => {
    setIsEdit(true);
    fetchDelivery(null, { id: record.id });
  };
  const handleDelete = (record: DeliveryProps) => {
    if (
      window.confirm(
        intl.formatMessage({
          id: 'message.confirm_delete_delivery',
        })
      )
    ) {
      deleteDelivery(null, { id: record.id });
    }
  };

  const handleReload = () => {
    setReloadCount((prev) => prev + 1);
  };

  const handleCreateDelivery = () => {
    form.resetFields();
    setIsOpenCreateDelivery(true);
  };

  const handleCloseDeliveryDrawer = () => {
    form.resetFields();
    setIsOpenCreateDelivery(false);
    setIsEdit(false);
  };

  const initialValues = {
    open_hours: convertToDayJs(options.open_hours),
    status: false,
  };

  const columns = useMemo(() => {
    return [
      {
        title: intl.formatMessage({ id: 'general.name' }),
        dataIndex: 'name',
        render: (value: string) => value,
      },
      {
        title: intl.formatMessage({ id: 'general.delivery_type' }),
        dataIndex: 'type',
        render: (value: string) => {
          return intl.formatMessage({ id: `delivery.${value}` });
        },
      },
      {
        title: intl.formatMessage({ id: 'general.address' }),
        dataIndex: 'address',
        render: (value: string) => value,
      },
      {
        title: intl.formatMessage({ id: 'general.phone' }),
        dataIndex: 'phone',
        render: (value: string) => value,
      },
      {
        title: intl.formatMessage({ id: 'general.status' }),
        dataIndex: 'status',
        render: (value: boolean) => (
          <Checkbox checked={value} disabled={true} />
        ),
        className: 'centered-column',
      },
      can('edit_delivery')
        ? {
            title: intl.formatMessage({ id: 'general.actions' }),
            render: (record: DeliveryProps) => {
              const items: MenuProps['items'] = [
                {
                  key: 'edit',
                  label: intl.formatMessage({ id: 'general.edit' }),
                },
                {
                  key: 'delete',
                  label: intl.formatMessage({ id: 'general.delete' }),
                },
              ];

              const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
                switch (key) {
                  case 'edit':
                    handleEdit(record);
                    break;
                  case 'delete':
                    handleDelete(record);
                    break;
                }
              };

              return (
                <Dropdown
                  menu={{ items, onClick: handleMenuClick }}
                  trigger={['click']}
                  children={
                    <Button
                      label={intl.formatMessage({ id: 'general.actions' })}
                    />
                  }
                />
              );
            },
          }
        : null,
    ];
  }, []);

  const mobileItems = {
    coverFields: [
      {
        name: '_',
        render: (value: undefined, record: DeliveryProps) => {
          return (
            <>
              <span>{intl.formatMessage({ id: 'general.name' })}</span>
              {!(record.type === 'delivery' || record.type === 'self') && (
                <span>
                  {intl.formatMessage({
                    id: record.status
                      ? 'general.status_active'
                      : 'general.status_inactive',
                  })}
                </span>
              )}
            </>
          );
        },
      },
      {
        name: 'type',
        render: (value: string, record: DeliveryProps) => {
          return (
            <>
              <span>{intl.formatMessage({ id: `delivery.${value}` })}</span>
              {!(record.type === 'delivery' || record.type === 'self') && (
                <Checkbox checked={record.status} disabled={true} />
              )}
            </>
          );
        },
      },
    ],
    bodyFields: [
      {
        name: 'address',
        render: (value: string, record: DeliveryProps) => {
          if (record.type === 'delivery' || record.type === 'self') {
            return (
              <>
                <span>{intl.formatMessage({ id: 'general.address' })}</span>
                <span>{value}</span>
              </>
            );
          }
          return null;
        },
      },
      {
        name: 'type',
        render: (value: string, record: DeliveryProps) => {
          if (record.type === 'delivery' || record.type === 'self') {
            return (
              <>
                <span>
                  {intl.formatMessage({
                    id: record.status
                      ? 'general.status_active'
                      : 'general.status_inactive',
                  })}
                </span>
                <Checkbox checked={record.status} disabled={true} />
              </>
            );
          }
          return null;
        },
      },
    ],
    footerFields: can('edit_delivery')
      ? [
          {
            name: '_',
            render: (value: undefined, record: DeliveryProps) => {
              const items: MenuProps['items'] = [
                {
                  key: 'edit',
                  label: intl.formatMessage({ id: 'general.edit' }),
                },
                {
                  key: 'delete',
                  label: intl.formatMessage({ id: 'general.delete' }),
                },
              ];

              const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
                switch (key) {
                  case 'edit':
                    handleEdit(record);
                    break;
                  case 'delete':
                    handleDelete(record);
                    break;
                }
              };

              return (
                <Dropdown
                  menu={{ items, onClick: handleMenuClick }}
                  trigger={['click']}
                  children={
                    <Button
                      label={intl.formatMessage({ id: 'general.actions' })}
                    />
                  }
                />
              );
            },
          },
        ]
      : [],
  };

  const content = (
    <StyledFarmDeliveries>
      <Form form={form} initialValues={initialValues}>
        <div className={'farm-deliveries-header'}>
          <Button
            label={intl.formatMessage({ id: 'general.add_delivery' })}
            type={'primary'}
            onClick={handleCreateDelivery}
            ability={'edit_delivery'}
          />
          <DrawerCreateDelivery
            open={isOpenCreateDelivery}
            closable={true}
            onClose={handleCloseDeliveryDrawer}
            form={form}
            setReloadCount={handleReload}
            isEdit={isEdit}
            farmId={farmId}
          />
        </div>
        <ResponsiveWrapper
          desktopComponent={
            <Table
              url={'/api/v2/farms/deliveries'}
              columns={columns}
              filter={stableFilter}
              reload={reloadCount}
              page_size={10}
            />
          }
          mobileComponent={
            <MobileList
              url={'/api/v2/farms/deliveries'}
              bodyFields={mobileItems.bodyFields}
              coverFields={mobileItems.coverFields}
              footerFields={mobileItems.footerFields}
              filter={stableFilter}
              reload={reloadCount}
              page_size={10}
            />
          }
        />
      </Form>
    </StyledFarmDeliveries>
  );

  return (
    <PageLayoutWrapper className={!showPageLayout ? 'hide-page-layout' : ''}>
      <StyledMobileHeaderWrapper>
        <Button
          type={'text'}
          label={`+ ${intl.formatMessage({ id: 'delivery.add_delivery' })}`}
          onClick={() => handleCreateDelivery()}
          ability={'edit_delivery'}
          icon={
            <Icon
              name={'/admin/admin-create-delivery'}
              className="admin-header-item-icon"
            />
          }
        />
        <div className="mobile-header-item-wrapper" />
      </StyledMobileHeaderWrapper>
      <DefaultLayout.PageLayout
        breadcrumbs={
          showPageLayout
            ? [
                {
                  title: intl.formatMessage({ id: 'navigation.beginning' }),
                },
                {
                  title: intl.formatMessage({ id: 'navigation.deliveries' }),
                },
              ]
            : undefined
        }
      >
        <DefaultLayout.PageContent>{content}</DefaultLayout.PageContent>
      </DefaultLayout.PageLayout>
    </PageLayoutWrapper>
  );
};

export default FarmDeliveries;
